package co.maytes.standupbot.dashboard.pages.app.standups.sections

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.Card
import co.maytes.standupbot.dashboard.components.widgets.FormRow
import co.maytes.standupbot.dashboard.components.widgets.SectionCard
import co.maytes.standupbot.dashboard.components.widgets.UpgradeNudge
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideClock
import co.maytes.standupbot.dashboard.components.widgets.icons.LucidePlay
import co.maytes.standupbot.dashboard.features.standupruns.StandupListItem
import co.maytes.standupbot.dashboard.features.standups.ChannelPicker
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.standup.StandupDto
import co.maytes.standupbot.shared.dto.standup.UpdateStandupRequestDto
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier

@Composable
internal fun OverviewTab(
    standup: StandupDto,
    workspaceId: String,
    inFlight: Boolean,
    onSave: (UpdateStandupRequestDto) -> Unit,
) {
    var name by remember(standup.id) { mutableStateOf(standup.name) }
    var channelId by remember(standup.id) { mutableStateOf(standup.slackChannelId) }
    var channelName by remember(standup.id) { mutableStateOf(standup.slackChannelName) }
    var enabled by remember(standup.id) { mutableStateOf(standup.enabled) }

    val nameDirty = name.trim() != standup.name && name.trim().isNotBlank()
    val channelDirty = channelId != standup.slackChannelId && channelId.isNotBlank()
    val enabledDirty = enabled != standup.enabled
    val dirty = nameDirty || channelDirty || enabledDirty

    SectionCard(
        title = "Overview",
        description = "Name, destination channel, and run state for this standup.",
    ) {
        Column(modifier = Modifier.fillMaxWidth().gap(Spacing.lg)) {
            standup.upgradeNudge?.let { UpgradeNudge(it) }
            FormRow(label = "Name") {
                org.jetbrains.compose.web.dom.Input(
                    type = org.jetbrains.compose.web.attributes.InputType.Text,
                    attrs = co.maytes.standupbot.dashboard.components.widgets.TextInputStyle.toModifier()
                        .fillMaxWidth()
                        .toAttrs {
                            value(name)
                            onInput { name = it.value }
                            if (inFlight) attr("disabled", "disabled")
                        },
                )
            }

            FormRow(label = "Slack channel") {
                ChannelPicker(
                    workspaceId = workspaceId,
                    selectedId = channelId,
                    selectedName = channelName,
                    onChange = { id, nm ->
                        channelId = id
                        channelName = nm
                    },
                )
            }

            EnabledStatePanel(
                enabled = enabled,
                inFlight = inFlight,
                onToggle = { enabled = !enabled },
            )

            Row(
                modifier = Modifier.fillMaxWidth().gap(Spacing.sm),
                horizontalArrangement = Arrangement.End,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Button(
                    label = "Reset",
                    variant = ButtonVariant.Ghost,
                    enabled = dirty && !inFlight,
                    onClick = {
                        name = standup.name
                        channelId = standup.slackChannelId
                        channelName = standup.slackChannelName
                        enabled = standup.enabled
                    },
                )
                Button(
                    label = if (inFlight) "Saving…" else "Save",
                    variant = ButtonVariant.Primary,
                    enabled = dirty && !inFlight,
                    onClick = {
                        onSave(
                            UpdateStandupRequestDto(
                                name = if (nameDirty) name.trim() else null,
                                slackChannelId = if (channelDirty) channelId else null,
                                slackChannelName = if (channelDirty) channelName else null,
                                enabled = if (enabledDirty) enabled else null,
                            ),
                        )
                    },
                )
            }
        }
    }
}

@Composable
private fun EnabledStatePanel(
    enabled: Boolean,
    inFlight: Boolean,
    onToggle: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .borderRadius(Radius.md)
            .padding(Spacing.md)
            .styleModifier {
                property("background-color", (if (enabled) Palette.accentSubtle else Palette.surfaceSubtle).cssRgb())
                property("border", "1px solid ${(if (enabled) Palette.accent else Palette.border).cssRgb()}")
                property("gap", "12px")
            },
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Row(
            modifier = Modifier.gap(Spacing.sm).styleModifier { property("min-width", "0") },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier.styleModifier {
                    property("width", "28px")
                    property("height", "28px")
                    property("border-radius", "8px")
                    property("background-color", (if (enabled) Palette.accent else Palette.surface).cssRgb())
                    property("color", (if (enabled) Palette.surface else Palette.textTertiary).cssRgb())
                    property("display", "inline-flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("flex-shrink", "0")
                    if (!enabled) property("border", "1px solid ${Palette.border.cssRgb()}")
                },
            ) { if (enabled) LucidePlay(size = 15) else LucideClock(size = 15) }
            Column(modifier = Modifier.gap(Spacing.xxs).styleModifier { property("min-width", "0") }) {
                SpanText(
                    text = if (enabled) "Active" else "Paused",
                    modifier = Modifier
                        .fontSize(Typography.body.size)
                        .fontWeight(600)
                        .color(Palette.textPrimary),
                )
                SpanText(
                    text = if (enabled)
                        "This standup fires on the schedule below."
                    else
                        "This standup is paused — Quartz won't fire it until you resume.",
                    modifier = Modifier
                        .fontSize(Typography.bodySmall.size)
                        .color(Palette.textSecondary),
                )
            }
        }
        Button(
            label = if (enabled) "Pause" else "Resume",
            variant = if (enabled) ButtonVariant.Secondary else ButtonVariant.Primary,
            enabled = !inFlight,
            onClick = onToggle,
        )
    }
}

@Composable
internal fun RecentRunsPanel(
    standup: StandupDto,
    runs: List<co.maytes.standupbot.shared.dto.standuprun.StandupRunSummaryDto>,
    onViewRun: (String) -> Unit,
) {
    Column(modifier = Modifier.fillMaxWidth().gap(Spacing.sm)) {
        SpanText(
            text = "Recent runs",
            modifier = Modifier
                .fontSize(Typography.h3.size)
                .fontWeight(Typography.h3.weight)
                .color(Palette.textPrimary),
        )
        if (runs.isEmpty()) {
            Card(modifier = Modifier.fillMaxWidth()) {
                SpanText(
                    text = "No runs yet for this standup.",
                    modifier = Modifier
                        .fontSize(Typography.bodySmall.size)
                        .color(Palette.textSecondary),
                )
            }
        } else {
            Column(modifier = Modifier.fillMaxWidth()) {
                runs.forEach { run ->
                    StandupListItem(
                        item = run,
                        standup = standup,
                        onClick = onViewRun,
                    )
                }
            }
        }
    }
}

@Composable
internal fun EmptyRosterBanner(onAddMembers: () -> Unit) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(Palette.surfaceSubtle)
            .margin(top = Spacing.md),
        padded = false,
    ) {
        Row(
            modifier = Modifier.padding(Spacing.md).gap(Spacing.sm),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                text = "Add members to enable this standup",
                modifier = Modifier
                    .fontSize(Typography.bodySmall.size)
                    .color(Palette.warning)
                    .styleModifier { property("flex", "1") },
            )
            Button(
                label = "Add members",
                variant = ButtonVariant.Secondary,
                size = ButtonSize.Sm,
                onClick = onAddMembers,
            )
        }
    }
}
